import { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
    IconAlertTriangle,
    IconDownload,
    IconRefresh,
} from '@tabler/icons-react';

import { TableContainer } from '../../Styles/CustomViews/Pages/ListComponent/customListComponentStyles';
import { AutoBackupConfigContainer, InstanceDetailsContainer, SPTableContainer } from '../../Styles/InstanceManagement/instanceManagementStyles';

import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';
import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import DateHelper from '../../Utilities/Helpers/DateHelper';
import GlobalFunctionHelper from '../../Utilities/Helpers/GlobalFunctionHelper';

import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { OrderByEnum } from '../../Utilities/Enums/SortEnums';
import { InstanceAutoBackupsField, InstanceAutoBackupsListField } from '../../Utilities/Enums/ParametersEnum';
import { InstanceAutoBackupStatusEnum, InstanceAutoBackupStatusTextEnum, InstanceAutoBackupTypeEnum, InstanceAutoBackupTypeTextEnum, InstanceBackupConfigFrequencyEnum, InstanceBackupConfigFrequencyTextEnum, InstanceQueryTypeEnum } from '../../Utilities/Enums/StatusEnums';

import { ErrorMessage, SuccessMessage } from '../../Constants/Messages';

import CustomListComponent from '../../CustomViews/Pages/ListComponent/CustomListComponent';
import CustomIconButton from '../../CustomViews/Elements/CustomIconButton';
import BackupRestoreConfirmationModal from './BackupRestoreConfirmationModal';

import { InstanceAutoBackUpInterface, InstanceAutoBackupsList } from '../../Models/InstanceManagement/InstanceManagement';

import { useInstanceManagementHook } from '../../Hooks/InstanceManagement/instanceManagementHooks';

export default function AutoBackups(props: InstanceAutoBackUpInterface) {
    let { instanceName, autoBackupConfig, previewAutoBackupConfig, setautoBackupConfig, setpreviewAutoBackupConfig, triggeredQuery, settriggeredQuery } = props;
    let location = useLocation();
    let queryParams = new URLSearchParams(location.search);
    let instanceId = queryParams.get('id') ?? "0";
    let isDarkMode = LocalStorageHelper.isDarkMode();
    const { enqueueSnackbar } = useSnackbar();

    // states
    const [queryStr, setQueryStr] = useState({
        pageSize: QueryBuilderHelper.DefaultPageSizeValue(),
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
        status: QueryBuilderHelper.DefaultQueryFilterValue('status') ?? 0,
        sortBy: QueryBuilderHelper.DefaultSortValue(InstanceAutoBackupsListField.CREATED_DATE),
        orderBy: QueryBuilderHelper.DefaultOrderValue(OrderByEnum.DESC),
    });
    let [openBackupRestoreConfirmationModal, setopenBackupRestoreConfirmationModal]: any = useState<boolean>(false);
    let [selectedRecord, setselectedRecord]: any = useState<InstanceAutoBackupsList>(new InstanceAutoBackupsList());

    const q_queryKey = [CachingEnum.INSTANCE_AUTO_BACKUP_LIST, queryStr]
    const { data, fetchStatus, instancesInvalidateQueries, downloadInstanceBackupById, restoreInstanceBackupById } = useInstanceManagementHook(q_queryKey, queryStr, true, InstanceQueryTypeEnum.AUTO_BACKUP, instanceId);

    const tableColumns = [
        {
            accessor: InstanceAutoBackupsListField.BACKUP_DATE,
            title: 'When',
            sortable: true,
            render: (record: InstanceAutoBackupsList) => {
                let backupDate: string | Date | null = record?.[InstanceAutoBackupsListField.BACKUP_DATE] != null && record?.[InstanceAutoBackupsListField.BACKUP_DATE] != "" ? DateHelper.getFormattedDate(record?.[InstanceAutoBackupsListField.BACKUP_DATE], "dd/MM/yyyy hh:mm a") : null;
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className='alDateText'>{backupDate ?? '--'}</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: InstanceAutoBackupsListField.TYPE,
            title: 'Type',
            sortable: true,
            render: (record: InstanceAutoBackupsList) => {
                let abType: InstanceAutoBackupTypeEnum = record?.[InstanceAutoBackupsListField.TYPE] ?? InstanceAutoBackupTypeEnum.AUTOMATIC;
                let abTypeText: string = InstanceAutoBackupTypeTextEnum[abType];
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className='alDateText'>{abTypeText ?? '--'}</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: InstanceAutoBackupsListField.SIZE_GB,
            title: 'Size',
            sortable: true,
            render: (record: InstanceAutoBackupsList) => {
                let sizeGB: string | number = record?.[InstanceAutoBackupsListField.SIZE_GB] ?? 0;
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className='alDateText'>{sizeGB ?? '0'} GB</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: InstanceAutoBackupsListField.DURATION_SECONDS,
            title: 'Duration',
            sortable: true,
            render: (record: InstanceAutoBackupsList) => {
                let duration: string | number = record?.[InstanceAutoBackupsListField.DURATION_SECONDS] ?? 0;
                let durationInMinutes: string = GlobalFunctionHelper.formatTime(duration);
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className='alDateText'>{durationInMinutes}</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: InstanceAutoBackupsListField.STATUS,
            title: 'Status',
            sortable: true,
            render: (record: InstanceAutoBackupsList) => {
                let status: InstanceAutoBackupStatusEnum = record?.[InstanceAutoBackupsListField.STATUS] ?? InstanceAutoBackupStatusEnum.SUCCESS;
                let statusText = InstanceAutoBackupStatusTextEnum[status];
                
                return <InstanceDetailsContainer $darkMode={isDarkMode}>
                    <span className={'spStatus ' + statusText?.toLowerCase()}><span className='dotText'>&nbsp;</span>{statusText}</span>
                </InstanceDetailsContainer>
            }
        },
        {
            accessor: '#',
            title: "Action",
            render: (record: InstanceAutoBackupsList) => <>
                {record?.[InstanceAutoBackupsListField.STATUS] == InstanceAutoBackupStatusEnum.SUCCESS && <CustomIconButton toolTipName='Download'
                    icon={<IconDownload />}
                    onClick={async (e: any) => {
                        await handleDownloadBackup(e, record);
                    }} /> }

                {record?.[InstanceAutoBackupsListField.STATUS] == InstanceAutoBackupStatusEnum.SUCCESS && <CustomIconButton toolTipName='Restore'
                    icon={<IconRefresh />}
                    onClick={async (e: any) => {
                        setselectedRecord(record);
                        setopenBackupRestoreConfirmationModal(true);
                    }} /> }
            </>
        },
    ];

    //Event Handlers
    const handleDownloadBackup = async (e: any, record: InstanceAutoBackupsList) => {
        enqueueSnackbar(SuccessMessage.PREPARING_BACKUP_DOWNLOAD, { variant: 'info' });

        e.preventDefault();

        let message: string = ErrorMessage.PROBLEM_BACKUP_DOWNLOAD;
        let success: boolean = false;
        let bakFile: any = null;

        if(instanceId != null && instanceId != "" && record?.[InstanceAutoBackupsListField.PUBLIC_ID] != null && record?.[InstanceAutoBackupsListField.PUBLIC_ID] != "") {
            bakFile = await downloadInstanceBackupById(instanceId, record?.[InstanceAutoBackupsListField.PUBLIC_ID] ?? "", true, true);
        }

        if (bakFile?.data != null && bakFile?.data != "") {
            const rawData: string = bakFile.data;
            const base64 = rawData.includes(',') ? rawData.substring(rawData.indexOf(',') + 1) : rawData;

            const byteChars = atob(base64);
            const byteArray = new Uint8Array(byteChars.length);
            for (let i = 0; i < byteChars.length; i++) {
                byteArray[i] = byteChars.charCodeAt(i);
            }
            const blob = new Blob([byteArray], { type: 'application/zip' });

            const iName = instanceName != null && instanceName != "" ? instanceName : null;
            const backupDate = (record?.[InstanceAutoBackupsListField.BACKUP_DATE] != null && record?.[InstanceAutoBackupsListField.BACKUP_DATE] != "" ?
                DateHelper.getFormattedDate( record?.[InstanceAutoBackupsListField.BACKUP_DATE], "yyyyMMdd_HHmmss") : null
            );
            const fileNamePart = (iName ?? 'instance-backup') + (backupDate ? "-" + backupDate : "");
            const fileName = `${fileNamePart}.zip`;

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            message = SuccessMessage.BACKUP_SUCCESSFULLY_DOWNLOADED;
            success = true;
        }

        enqueueSnackbar(message, { variant: success ? "success" : "error" });
    }

    const handleRestoreBackup = async () => {
        if(settriggeredQuery) {
            settriggeredQuery(CachingEnum.INSTANCE_AUTO_BACKUP_LIST);
        }

        let record: InstanceAutoBackupsList = selectedRecord ?? new InstanceAutoBackupsList();

        enqueueSnackbar(SuccessMessage.PREPARING_BACKUP_RESTORE, { variant: 'info' });

        let message: string = ErrorMessage.PROBLEM_BACKUP_RESTORE;
        let success: boolean = false;
        let restoreBackup: any = null;

        if(instanceId != null && instanceId != "" && record?.[InstanceAutoBackupsListField.PUBLIC_ID] != null && record?.[InstanceAutoBackupsListField.PUBLIC_ID] != "") {
            restoreBackup = await restoreInstanceBackupById(instanceId, record?.[InstanceAutoBackupsListField.PUBLIC_ID] ?? "", true)
            
            // if(restoreBackup?.isSuccess) {
                
            // }
        } else {
            enqueueSnackbar(message, { variant: success ? "success" : "error" });
        }
    }

    useEffect(() => {
        instancesInvalidateQueries();
    }, [instanceId]);
    
    //Rendering Functions
    const renderMaintenanceModeConfirmationModal = () => {
        return <BackupRestoreConfirmationModal
            selectedRecord={selectedRecord}
            openDialog={openBackupRestoreConfirmationModal}
            handleClose={() => { setopenBackupRestoreConfirmationModal(false); }}
            buttonAction={() => {
                setopenBackupRestoreConfirmationModal(false);
                handleRestoreBackup();
            }}
            instanceName={instanceName != null && instanceName != "" ?
                instanceName : null
            } />
    }

    const renderBackupConfig = () => {
        let frequency: InstanceBackupConfigFrequencyEnum = previewAutoBackupConfig?.[InstanceAutoBackupsField.FREQUENCY] ?? InstanceBackupConfigFrequencyEnum.DAILY;
        let frequencyText: string = InstanceBackupConfigFrequencyTextEnum[frequency];
        let preferredTime: string | Date | null = (previewAutoBackupConfig?.[InstanceAutoBackupsField.PREFERRED_TIME] != null && previewAutoBackupConfig?.[InstanceAutoBackupsField.PREFERRED_TIME] != "" ?
            DateHelper.getFormattedDate(new Date(previewAutoBackupConfig?.[InstanceAutoBackupsField.PREFERRED_TIME]), "HH:mm") : null
        )
        let encryption: string | null = null;
        let nextBackup: string | Date | null = null;

        return <AutoBackupConfigContainer $darkMode={isDarkMode}>
            {previewAutoBackupConfig?.[InstanceAutoBackupsField.ENABLED] && <>
                <div className='configFieldItem'>
                    <span className='fieldTitle'>Schedule</span>
                    <span className='fieldValue'>{frequencyText ?? "--"} . {preferredTime ?? "--"}</span>
                </div>
                <div className='configFieldItem'>
                    <span className='fieldTitle'>Retention</span>
                    <span className='fieldValue'>{previewAutoBackupConfig?.[InstanceAutoBackupsField.RETENTION_DAYS] ?? "0"} Days</span>
                </div>
                <div className='configFieldItem'>
                    <span className='fieldTitle'>Destination</span>
                    <span className='fieldValue'>{previewAutoBackupConfig?.[InstanceAutoBackupsField.DESTINATION] ?? "--"}</span>
                </div>

                <div className='configFieldItem'>
                    <span className='fieldTitle'>Encryption</span>
                    <span className='fieldValue'>{encryption ?? "--"}</span>
                </div>
                <div className='configFieldItem'>
                    <span className='fieldTitle'>Point-in-time Restore</span>
                    <span className='fieldValue'>{previewAutoBackupConfig?.[InstanceAutoBackupsField.POINT_IN_TIME_RESTORE] ? "Enabled" : "Not enabled"}</span>
                </div>
                <div className='configFieldItem'>
                    <span className='fieldTitle'>Next Backup</span>
                    <span className='fieldValue'>{nextBackup ?? "--"}</span>
                </div>
            </>}

            {!previewAutoBackupConfig?.[InstanceAutoBackupsField.ENABLED] && <div className='disabledAutoBackup threeRowSpan'>
                <div className='iconContainer'><IconAlertTriangle /></div>
                <span className='alertMessage'>Auto-backup is disabled for this instance.</span>
                <span className='alertMessage'>Enable it to protect production data.</span>
            </div> }

            <div className='configFieldItem threeRowSpan'>
                <span className='fieldTitle backupHistoryText'>Backup History</span>
            </div>
        </AutoBackupConfigContainer>
    }
    
    
    return <SPTableContainer>
        {renderBackupConfig()}

        <TableContainer elevation={2} $isNotBorderRadiusAll={true}>
            <CustomListComponent
                fetching={(fetchStatus == "fetching" && triggeredQuery == CachingEnum.INSTANCE_AUTO_BACKUP_LIST) || (fetchStatus == "fetching" && triggeredQuery == 'all')}
                striped
                columns={tableColumns}
                idAccessor={(record: InstanceAutoBackupsList) => `instanceAutoBackups${record?.[InstanceAutoBackupsListField.PUBLIC_ID]}`}
                records={data?.data?.list ?? []}
                totalRecords={data?.data?.metaData?.totalCount ?? 0}
                page={queryStr.pageNumber}
                recordsPerPage={queryStr.pageSize}
                defaultColumnSort={{ columnAccessor: queryStr?.sortBy ?? 'id', direction: queryStr?.orderBy ?? 'asc' }}
                onSortStatusChange={((sort: any) => { 
                    if(settriggeredQuery) {
                        settriggeredQuery(CachingEnum.INSTANCE_AUTO_BACKUP_LIST);
                    }

                    setQueryStr((prev: any) => ({ ...prev, sortBy: sort?.columnAccessor ?? "id", orderBy: sort?.direction ?? "asc", pageNumber: 1 }));
                })}
                setPage={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageNumber: p }))}
                onRecordsPerPageChange={(p: number) => setQueryStr((prev: any) => ({ ...prev, pageSize: p, pageNumber: 1 }))} />
        </TableContainer>

        {openBackupRestoreConfirmationModal && renderMaintenanceModeConfirmationModal()}
    </SPTableContainer>
}
